/**
 * RV Match Application Form
 * A structured, step-by-step form to collect user information and RV preferences
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  User, Mail, Phone, MapPin, DollarSign, Users, Truck, 
  Tent, Compass, ChevronRight, ChevronLeft, Check, Loader2,
  Home, Car, PawPrint, Baby, Calendar, Zap, Droplets, UtensilsCrossed,
  Wifi, Sun, Thermometer, Bath
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import EducationalLightbox, { EduInfoButton } from '../components/EducationalLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// RV Types
const RV_TYPES = [
  { id: 'travel_trailer', label: 'Travel Trailer', icon: '🚐', desc: 'Towable, most popular choice' },
  { id: 'fifth_wheel', label: 'Fifth Wheel', icon: '🏠', desc: 'Spacious, needs truck with hitch' },
  { id: 'class_a', label: 'Class A Motorhome', icon: '🚌', desc: 'Largest, most luxurious' },
  { id: 'class_c', label: 'Class C Motorhome', icon: '🚐', desc: 'Medium size, over-cab bed' },
  { id: 'class_b', label: 'Class B / Camper Van', icon: '🚙', desc: 'Compact, easy to drive' },
  { id: 'toy_hauler', label: 'Toy Hauler', icon: '🏍️', desc: 'Garage for ATVs/bikes' },
  { id: 'pop_up', label: 'Pop-Up / Folding', icon: '⛺', desc: 'Lightweight, affordable' },
  { id: 'truck_camper', label: 'Truck Camper', icon: '🛻', desc: 'Goes in truck bed' },
];

// Budget Ranges
const BUDGET_RANGES = [
  { id: 'under_20k', label: 'Under $20,000', value: 20000 },
  { id: '20k_35k', label: '$20,000 - $35,000', value: 35000 },
  { id: '35k_50k', label: '$35,000 - $50,000', value: 50000 },
  { id: '50k_75k', label: '$50,000 - $75,000', value: 75000 },
  { id: '75k_100k', label: '$75,000 - $100,000', value: 100000 },
  { id: '100k_150k', label: '$100,000 - $150,000', value: 150000 },
  { id: 'over_150k', label: '$150,000+', value: 200000 },
];

// Sleep Capacity
const SLEEP_OPTIONS = [
  { id: '2', label: '2 People', icon: <Users className="w-5 h-5" /> },
  { id: '3-4', label: '3-4 People', icon: <Users className="w-5 h-5" /> },
  { id: '5-6', label: '5-6 People', icon: <Users className="w-5 h-5" /> },
  { id: '7+', label: '7+ People', icon: <Users className="w-5 h-5" /> },
];

// Camping Style
const CAMPING_STYLES = [
  { id: 'campground', label: 'Campgrounds with Hookups', icon: <Zap className="w-5 h-5" />, desc: 'Full hookups, amenities' },
  { id: 'boondocking', label: 'Off-Grid / Boondocking', icon: <Compass className="w-5 h-5" />, desc: 'Self-sufficient camping' },
  { id: 'both', label: 'Both', icon: <Tent className="w-5 h-5" />, desc: 'Flexible camping style' },
];

// Trip Types
const TRIP_TYPES = [
  { id: 'weekend', label: 'Weekend Trips', icon: <Calendar className="w-5 h-5" /> },
  { id: 'week', label: 'Week-Long Trips', icon: <Calendar className="w-5 h-5" /> },
  { id: 'extended', label: 'Extended Travel', icon: <Calendar className="w-5 h-5" /> },
  { id: 'fulltime', label: 'Full-Time Living', icon: <Home className="w-5 h-5" /> },
];

// Must-Have Features
const FEATURES = [
  { id: 'bathroom', label: 'Full Bathroom', icon: <Bath className="w-4 h-4" /> },
  { id: 'outdoor_kitchen', label: 'Outdoor Kitchen', icon: <UtensilsCrossed className="w-4 h-4" /> },
  { id: 'bunk_beds', label: 'Bunk Beds', icon: <Users className="w-4 h-4" /> },
  { id: 'king_bed', label: 'King/Queen Bed', icon: <Home className="w-4 h-4" /> },
  { id: 'solar', label: 'Solar Ready', icon: <Sun className="w-4 h-4" /> },
  { id: 'fireplace', label: 'Fireplace', icon: <Thermometer className="w-4 h-4" /> },
  { id: 'washer_dryer', label: 'Washer/Dryer', icon: <Droplets className="w-4 h-4" /> },
  { id: 'slide_outs', label: 'Slide Outs', icon: <ChevronRight className="w-4 h-4" /> },
];

const RVMatchApplicationForm = () => {
  const { dealerSlug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dealer, setDealer] = useState(null);

  // Educational lightbox — string topic when open, null when closed. Wired
  // into section headers via the EduInfoButton pill.
  const [eduTopic, setEduTopic] = useState(null);
  const openEdu = (topic) => setEduTopic(topic);
  const closeEdu = () => setEduTopic(null);
  
  // Step 1: Contact Info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [zipcode, setZipcode] = useState('');
  
  // Step 2: RV Preferences
  const [rvType, setRvType] = useState('');
  const [budget, setBudget] = useState('');
  const [sleeps, setSleeps] = useState('');
  
  // Step 3: Travel Style
  const [campingStyle, setCampingStyle] = useState('');
  const [tripType, setTripType] = useState('');
  const [hasKids, setHasKids] = useState(null);
  const [hasPets, setHasPets] = useState(null);
  
  // Step 4: Towing & Features
  const [towingOption, setTowingOption] = useState(''); // 'tow' or 'motorhome'
  const [towVehicle, setTowVehicle] = useState({ year: '', make: '', model: '' });
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const TOTAL_STEPS = 4;

  // Fetch dealer info
  useEffect(() => {
    const fetchDealer = async () => {
      if (dealerSlug) {
        try {
          const response = await axios.get(`${API}/dealer/by-slug/${dealerSlug}`);
          setDealer(response.data);
        } catch (error) {
          console.error('Error fetching dealer:', error);
        }
      }
    };
    fetchDealer();
  }, [dealerSlug]);

  // Format phone number as user types
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!firstName.trim()) { toast.error('Please enter your first name'); return false; }
        if (!lastName.trim()) { toast.error('Please enter your last name'); return false; }
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { 
          toast.error('Please enter a valid email address'); return false; 
        }
        if (!phone.trim() || phone.replace(/\D/g, '').length < 10) { 
          toast.error('Please enter a valid phone number'); return false; 
        }
        if (!zipcode.trim() || zipcode.length < 5) { 
          toast.error('Please enter a valid ZIP code'); return false; 
        }
        return true;
      case 2:
        if (!rvType) { toast.error('Please select an RV type'); return false; }
        if (!budget) { toast.error('Please select a budget range'); return false; }
        if (!sleeps) { toast.error('Please select sleeping capacity'); return false; }
        return true;
      case 3:
        if (!campingStyle) { toast.error('Please select a camping style'); return false; }
        if (!tripType) { toast.error('Please select trip type'); return false; }
        return true;
      case 4:
        if (rvType && ['travel_trailer', 'fifth_wheel', 'toy_hauler'].includes(rvType)) {
          if (!towingOption) { toast.error('Please select towing preference'); return false; }
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(f => f !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    
    try {
      // Build the application data
      const applicationData = {
        // Contact Info
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`,
        email,
        phone: phone.replace(/\D/g, ''),
        zipcode,
        dealer_slug: dealerSlug,
        
        // RV Preferences
        matching_criteria: {
          rv_type: rvType,
          budget_max: BUDGET_RANGES.find(b => b.id === budget)?.value || 50000,
          sleep_capacity: parseInt(sleeps.split('-')[0]) || 2,
          camping_style: campingStyle,
          trip_type: tripType,
          has_kids: hasKids,
          has_pets: hasPets,
          must_have_features: selectedFeatures,
          condition_preference: 'either',
        },
        
        // Towing info (if applicable)
        tow_vehicle: towingOption === 'tow' && towVehicle.make ? {
          year: parseInt(towVehicle.year) || null,
          make: towVehicle.make,
          model: towVehicle.model,
        } : null,
        
        // Additional context
        user_responses: {
          'RV Type': RV_TYPES.find(t => t.id === rvType)?.label,
          'Budget Range': BUDGET_RANGES.find(b => b.id === budget)?.label,
          'Sleeping Capacity': sleeps,
          'Camping Style': CAMPING_STYLES.find(c => c.id === campingStyle)?.label,
          'Trip Type': TRIP_TYPES.find(t => t.id === tripType)?.label,
          'Has Kids': hasKids ? 'Yes' : 'No',
          'Has Pets': hasPets ? 'Yes' : 'No',
          'Must-Have Features': selectedFeatures.map(f => FEATURES.find(feat => feat.id === f)?.label).join(', '),
          'Additional Notes': additionalNotes,
        },
        
        additional_notes: additionalNotes,
      };

      // Submit to backend
      const response = await axios.post(`${API}/quiz/application-submit`, applicationData);
      
      if (response.data.success) {
        toast.success('Application submitted! Generating your matches...');
        
        // Navigate to matches page
        if (response.data.session_id) {
          navigate(`/my-matches/${response.data.session_id}`);
        } else {
          navigate('/my-quiz', { state: { email } });
        }
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Progress bar
  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} border-b backdrop-blur-sm`}>
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold">Find Your Perfect RV</h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {dealer?.business_name || 'RV Match AI'}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Step {currentStep} of {TOTAL_STEPS}
              </p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className={`h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Step 1: Contact Information */}
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="step-contact">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
                <User className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Let's Get Started</h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Tell us a bit about yourself so we can find your perfect RV match
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                    data-testid="input-first-name"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                    data-testid="input-last-name"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="text"
                    value={zipcode}
                    onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="90210"
                    maxLength={5}
                    className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                    data-testid="input-zipcode"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: RV Preferences */}
        {currentStep === 2 && (
          <div className="space-y-8" data-testid="step-rv-preferences">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
                <Truck className="w-8 h-8 text-blue-500" />
              </div>
              <div className="inline-flex items-center justify-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold">What Type of RV?</h2>
                <EduInfoButton topic="rv_types" onOpen={openEdu} isDark={isDark} label="RV types 101" />
              </div>
              <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Select your preferred RV style and budget
              </p>
            </div>

            {/* RV Type Selection */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                RV Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {RV_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setRvType(type.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      rvType === type.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`rv-type-${type.id}`}
                  >
                    <div className="text-2xl mb-2">{type.icon}</div>
                    <div className="font-medium text-sm">{type.label}</div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{type.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Selection */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Budget Range <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {BUDGET_RANGES.map((range) => (
                  <button
                    key={range.id}
                    onClick={() => setBudget(range.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      budget === range.id
                        ? 'border-green-500 bg-green-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`budget-${range.id}`}
                  >
                    <DollarSign className={`w-5 h-5 mb-2 ${budget === range.id ? 'text-green-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="font-medium text-sm">{range.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sleeping Capacity */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                How Many People Will Sleep in the RV? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {SLEEP_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSleeps(option.id)}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      sleeps === option.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`sleeps-${option.id}`}
                  >
                    {option.icon}
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Travel Style */}
        {currentStep === 3 && (
          <div className="space-y-8" data-testid="step-travel-style">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
                <Compass className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Your Travel Style</h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Tell us how you plan to use your RV
              </p>
            </div>

            {/* Camping Style */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Where Will You Camp? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {CAMPING_STYLES.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setCampingStyle(style.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      campingStyle === style.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`camping-${style.id}`}
                  >
                    <div className={`mb-2 ${campingStyle === style.id ? 'text-blue-500' : ''}`}>{style.icon}</div>
                    <div className="font-medium">{style.label}</div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{style.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Trip Type */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Trip Length <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TRIP_TYPES.map((trip) => (
                  <button
                    key={trip.id}
                    onClick={() => setTripType(trip.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      tripType === trip.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`trip-${trip.id}`}
                  >
                    <div className={`mb-2 ${tripType === trip.id ? 'text-blue-500' : ''}`}>{trip.icon}</div>
                    <div className="font-medium text-sm">{trip.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Kids & Pets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Traveling with Kids?
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setHasKids(true)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      hasKids === true
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="has-kids-yes"
                  >
                    <Baby className="w-5 h-5" />
                    <span>Yes</span>
                  </button>
                  <button
                    onClick={() => setHasKids(false)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      hasKids === false
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="has-kids-no"
                  >
                    <span>No</span>
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Traveling with Pets?
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setHasPets(true)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      hasPets === true
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="has-pets-yes"
                  >
                    <PawPrint className="w-5 h-5" />
                    <span>Yes</span>
                  </button>
                  <button
                    onClick={() => setHasPets(false)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      hasPets === false
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="has-pets-no"
                  >
                    <span>No</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Towing & Features */}
        {currentStep === 4 && (
          <div className="space-y-8" data-testid="step-features">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
                <Car className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Final Details</h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Help us narrow down the perfect match
              </p>
            </div>

            {/* Towing Option - only show for towable RV types */}
            {['travel_trailer', 'fifth_wheel', 'toy_hauler', 'pop_up', 'truck_camper'].includes(rvType) && (
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Do You Have a Tow Vehicle?
                  </label>
                  <EduInfoButton topic="tow_vehicle" onOpen={openEdu} isDark={isDark} label="Tow guide" />
                  <EduInfoButton topic="tow_weight" onOpen={openEdu} isDark={isDark} label="Tow weight explained" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => setTowingOption('tow')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      towingOption === 'tow'
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="towing-yes"
                  >
                    <Car className={`w-5 h-5 mb-2 ${towingOption === 'tow' ? 'text-blue-500' : ''}`} />
                    <div className="font-medium">Yes, I Have a Tow Vehicle</div>
                  </button>
                  <button
                    onClick={() => { setTowingOption('need'); setTowVehicle({ year: '', make: '', model: '' }); }}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      towingOption === 'need'
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="towing-no"
                  >
                    <Truck className={`w-5 h-5 mb-2 ${towingOption === 'need' ? 'text-blue-500' : ''}`} />
                    <div className="font-medium">No / Need Guidance</div>
                  </button>
                </div>

                {/* Tow Vehicle Details */}
                {towingOption === 'tow' && (
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
                    <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Tow Vehicle Details (Optional)
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        type="text"
                        value={towVehicle.year}
                        onChange={(e) => setTowVehicle(prev => ({ ...prev, year: e.target.value }))}
                        placeholder="Year"
                        className={isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}
                        data-testid="tow-year"
                      />
                      <Input
                        type="text"
                        value={towVehicle.make}
                        onChange={(e) => setTowVehicle(prev => ({ ...prev, make: e.target.value }))}
                        placeholder="Make"
                        className={isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}
                        data-testid="tow-make"
                      />
                      <Input
                        type="text"
                        value={towVehicle.model}
                        onChange={(e) => setTowVehicle(prev => ({ ...prev, model: e.target.value }))}
                        placeholder="Model"
                        className={isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}
                        data-testid="tow-model"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Must-Have Features */}
            <div>
              <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Must-Have Features (Select All That Apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {FEATURES.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => toggleFeature(feature.id)}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                      selectedFeatures.includes(feature.id)
                        ? 'border-blue-500 bg-blue-500/10'
                        : isDark 
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`feature-${feature.id}`}
                  >
                    {selectedFeatures.includes(feature.id) ? (
                      <Check className="w-4 h-4 text-blue-500" />
                    ) : (
                      feature.icon
                    )}
                    <span className="text-sm font-medium">{feature.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Anything Else We Should Know? (Optional)
              </label>
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Tell us about any specific needs, preferences, or questions..."
                rows={3}
                className={`w-full px-4 py-3 rounded-xl border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                data-testid="additional-notes"
              />
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-blue-50'}`}>
              <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Your RV Preferences Summary</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className={isDark ? 'text-gray-500' : 'text-gray-600'}>RV Type:</span> <span className="font-medium">{RV_TYPES.find(t => t.id === rvType)?.label || '-'}</span></div>
                <div><span className={isDark ? 'text-gray-500' : 'text-gray-600'}>Budget:</span> <span className="font-medium">{BUDGET_RANGES.find(b => b.id === budget)?.label || '-'}</span></div>
                <div><span className={isDark ? 'text-gray-500' : 'text-gray-600'}>Sleeps:</span> <span className="font-medium">{sleeps || '-'}</span></div>
                <div><span className={isDark ? 'text-gray-500' : 'text-gray-600'}>Style:</span> <span className="font-medium">{CAMPING_STYLES.find(c => c.id === campingStyle)?.label || '-'}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200 dark:border-gray-800">
          {currentStep > 1 ? (
            <Button
              variant="outline"
              onClick={prevStep}
              className="flex items-center gap-2"
              data-testid="btn-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < TOTAL_STEPS ? (
            <Button
              onClick={nextStep}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="btn-next"
            >
              Continue
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              data-testid="btn-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Finding Your Matches...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Get My RV Matches
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Educational lightbox — opens over the form when the user taps
          any "Learn more" pill (RV types, tow vehicle, tow weight). */}
      <EducationalLightbox
        topic={eduTopic}
        open={!!eduTopic}
        onClose={closeEdu}
        isDark={isDark}
      />
    </div>
  );
};

export default RVMatchApplicationForm;
